/**
 * @file validate.ts
 * @description Проверка палитры темы кабинета (phase19.31, db-design §3; SC3,
 * SC27): состав ключей версии 1, формат цветов и реестр пар контраста.
 *
 * Кто пользуется: сервер — при чтении палитры для кабинета (не прошла —
 * отдаётся «Commora») и в отчёте раздела платформы «Темы кабинета»; админка —
 * для строки «Все пары проходят»; модульный тест — проверяет все пресеты.
 *
 * Сообщения — для владельца платформы, человеческим языком:
 * «Текст на основной кнопке: 3,7 — нужно не меньше 4,5».
 *
 * Реестр пар — данные (OCP): новая пара — новая строка в
 * `CABINET_CONTRAST_PAIRS`, а не ветка в коде.
 *
 * Необязательные ключи (`CABINET_OPTIONAL_PALETTE_KEYS`, SC66 `gradient-warm`):
 * отсутствие — не ошибка, при наличии — проверка формата `#hex`; в реестр
 * пар контраста не входят (тип пары допускает только обязательные ключи).
 */

import { contrastRatio, formatContrast, parseHexColor, type RgbaColor } from "./contrast.js";
import {
  CABINET_COLOR_PATTERN,
  CABINET_PALETTE_KEYS_V1,
  CABINET_SCHEME_COLORS,
  isCabinetOptionalPaletteKey,
  isCabinetPaletteKey,
  TOKENS_SCHEMA,
  type CabinetRequiredPaletteKey,
  type CabinetSchemeColorKey,
  type CabinetThemeScheme,
} from "./keys.js";

/**
 * Цвет пары: из палитры шаблона или из фиксированного набора схемы. Только
 * обязательные ключи: необязательные (`gradient-warm` — декор фона, SC66)
 * в проверку контраста текста не входят — их может не быть в палитре.
 */
type PairColorRef = CabinetRequiredPaletteKey | { scheme: CabinetSchemeColorKey };

/** Строка реестра пар контраста. */
export interface CabinetContrastPair {
  /** Что видит человек — начало сообщения об ошибке. */
  readonly label: string;
  readonly foreground: PairColorRef;
  readonly background: CabinetRequiredPaletteKey;
  /** На что ложится полупрозрачная подложка (подложка наведения → панель). */
  readonly base: CabinetRequiredPaletteKey;
  /** Порог WCAG: 4,5 — текст, 3 — нетекстовые элементы (рамка, кольцо). */
  readonly min: number;
  /** `warning` — не блокирует сохранение, показывается предупреждением. */
  readonly level: "error" | "warning";
}

/** Код замечания — для кабинета (иконка, группировка), текст — в `message`. */
export type CabinetPaletteIssueCode =
  | "palette_schema_unsupported"
  | "palette_not_object"
  | "palette_key_missing"
  | "palette_key_unknown"
  | "palette_color_invalid"
  | "palette_contrast_low";

/** Одно замечание проверки. */
export interface CabinetPaletteIssue {
  code: CabinetPaletteIssueCode;
  /** Готовый текст для владельца: «Текст на основной кнопке: 3,7 — нужно не меньше 4,5». */
  message: string;
  /** Ключ палитры (ошибки состава и формата). */
  key?: string;
  /** Пара контраста (только `palette_contrast_low`). */
  pair?: { foreground: string; background: string };
  /** Фактический контраст, два знака (только `palette_contrast_low`). */
  ratio?: number;
  /** Порог пары (только `palette_contrast_low`). */
  min?: number;
}

/** Итог проверки: `errors` блокируют, `warnings` — нет. */
export interface CabinetPaletteReport {
  errors: CabinetPaletteIssue[];
  warnings: CabinetPaletteIssue[];
}

const TEXT = 4.5;
const NON_TEXT = 3;

/** Хелпер реестра: пара «текст на подложке» с базой по умолчанию. */
function pair(
  label: string,
  foreground: PairColorRef,
  background: CabinetRequiredPaletteKey,
  min: number,
  options: { base?: CabinetRequiredPaletteKey; level?: "error" | "warning" } = {},
): CabinetContrastPair {
  return {
    label,
    foreground,
    background,
    base: options.base ?? "background",
    min,
    level: options.level ?? "error",
  };
}

/** Реестр пар контраста, db-design §3. */
export const CABINET_CONTRAST_PAIRS: readonly CabinetContrastPair[] = [
  // ── Обязательные ≥ 4,5: основной текст ──
  pair("Основной текст на фоне страницы", "on-surface", "background", TEXT),
  pair("Основной текст на карточке", "on-surface", "surface-lowest", TEXT),
  pair("Основной текст на приглушённой поверхности", "on-surface", "surface-low", TEXT),
  pair("Текст в поле ввода", "on-surface", "input", TEXT),
  pair("Текст строки таблицы при наведении", "on-surface", "row-hover", TEXT),
  pair("Текст выбранной строки таблицы", "on-surface", "row-selected", TEXT),
  pair("Текст в шапке таблицы", "on-surface", "table-head-bg", TEXT),
  // ── второстепенный и приглушённый текст ──
  pair("Второстепенный текст на карточке", "on-surface-variant", "surface-lowest", TEXT),
  pair("Второстепенный текст на фоне страницы", "on-surface-variant", "background", TEXT),
  pair("Приглушённый текст на карточке", "on-surface-muted", "surface-lowest", TEXT),
  pair("Приглушённый текст на фоне страницы", "on-surface-muted", "background", TEXT),
  // ── основная и градиентная кнопка ──
  pair("Текст на основной кнопке", "on-primary", "primary", TEXT),
  pair("Текст на основной кнопке при наведении", "on-primary", "primary-hover", TEXT),
  pair("Текст на основной кнопке при нажатии", "on-primary", "primary-pressed", TEXT),
  pair("Текст на градиентной кнопке (начало градиента)", "on-primary", "gradient-start", TEXT),
  pair("Текст на градиентной кнопке (конец градиента)", "on-primary", "gradient-end", TEXT),
  // ── ссылки ──
  pair("Ссылка на карточке", "link", "surface-lowest", TEXT),
  pair("Ссылка на фоне страницы", "link", "background", TEXT),
  // ── выбранное и мягкая кнопка ──
  pair("Текст выбранного пункта и мягкой кнопки", "accent-foreground", "accent", TEXT),
  pair("Текст мягкой кнопки при наведении", "accent-foreground", "soft-hover", TEXT),
  pair("Текст мягкой кнопки при нажатии", "soft-pressed-fg", "soft-pressed", TEXT),
  // ── боковая панель (полупрозрачные подложки — поверх самой панели) ──
  pair("Пункты боковой панели", "sidebar-foreground", "sidebar", TEXT),
  pair("Подписи групп боковой панели", "sidebar-muted", "sidebar", TEXT),
  pair("Заголовки боковой панели", "sidebar-strong", "sidebar", TEXT),
  pair("Выбранный пункт боковой панели", "sidebar-accent-foreground", "sidebar-accent", TEXT, {
    base: "sidebar",
  }),
  pair("Пункт боковой панели при наведении", "sidebar-hover-foreground", "sidebar-hover", TEXT, {
    base: "sidebar",
  }),
  // ── подсказка ──
  pair("Текст подсказки", "tooltip-fg", "tooltip-bg", TEXT),
  // ── смешанные пары с цветами схемы (статусы общие, SC27) ──
  pair("Текст ошибки на карточке", { scheme: "destructive-text" }, "surface-lowest", TEXT),
  pair("Текст успеха на карточке", { scheme: "success" }, "surface-lowest", TEXT),
  pair("Текст предупреждения на карточке", { scheme: "warning" }, "surface-lowest", TEXT),
  // ── Обязательные ≥ 3: нетекстовые элементы ──
  pair("Рамка поля в фокусе", "field-border-focus", "input", NON_TEXT),
  pair("Кольцо фокуса на карточке", "ring", "surface-lowest", NON_TEXT),
  pair("Метка выбранной строки таблицы", "row-marker", "surface-lowest", NON_TEXT),
  pair("Кольцо фокуса в боковой панели", "sidebar-ring", "sidebar", NON_TEXT),
  // ── Предупреждение, не блокирует (риск §10 дизайнера) ──
  pair("Рамка поля ввода", "field-border", "input", NON_TEXT, { level: "warning" }),
];

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function refName(ref: PairColorRef): string {
  return typeof ref === "string" ? ref : ref.scheme;
}

/**
 * Проверка палитры шаблона схемы `scheme`.
 *
 * Сначала состав и формат: если хоть один ключ отсутствует или цвет не в
 * формате — контраст по такой паре не считается (сравнивать не с чем), но
 * остальные пары проверяются, чтобы владелец увидел все проблемы разом.
 */
export function validateCabinetPalette(
  scheme: CabinetThemeScheme,
  tokens: unknown,
): CabinetPaletteReport {
  const errors: CabinetPaletteIssue[] = [];
  const warnings: CabinetPaletteIssue[] = [];

  if (!isPlainObject(tokens)) {
    errors.push({
      code: "palette_not_object",
      message: "Палитра должна быть набором «название цвета → цвет»",
    });
    return { errors, warnings };
  }

  const colors = new Map<string, RgbaColor>();
  for (const key of CABINET_PALETTE_KEYS_V1) {
    const value = tokens[key];
    if (value === undefined) {
      // Необязательный ключ (SC66) может отсутствовать в палитрах, сохранённых
      // до его появления; если он есть — формат проверяется как у остальных.
      if (isCabinetOptionalPaletteKey(key)) continue;
      errors.push({ code: "palette_key_missing", key, message: `Не задан цвет «${key}»` });
      continue;
    }
    const parsed =
      typeof value === "string" && CABINET_COLOR_PATTERN.test(value) ? parseHexColor(value) : null;
    if (!parsed) {
      errors.push({
        code: "palette_color_invalid",
        key,
        message: `Цвет «${key}» записан неверно: нужен формат #rrggbb или #rrggbbaa`,
      });
      continue;
    }
    colors.set(key, parsed);
  }
  for (const key of Object.keys(tokens)) {
    if (!isCabinetPaletteKey(key)) {
      errors.push({
        code: "palette_key_unknown",
        key,
        message: `Лишний цвет «${key}» — в палитре этой версии его нет`,
      });
    }
  }

  const schemeColors = CABINET_SCHEME_COLORS[scheme];
  for (const row of CABINET_CONTRAST_PAIRS) {
    const fg =
      typeof row.foreground === "string"
        ? colors.get(row.foreground)
        : parseHexColor(schemeColors[row.foreground.scheme]) ?? undefined;
    const bg = colors.get(row.background);
    const base = colors.get(row.base);
    if (!fg || !bg || !base) continue;
    const ratio = contrastRatio(fg, bg, base);
    if (ratio >= row.min) continue;
    const issue: CabinetPaletteIssue = {
      code: "palette_contrast_low",
      message: `${row.label}: ${formatContrast(ratio)} — нужно не меньше ${formatContrast(row.min)}`,
      pair: { foreground: refName(row.foreground), background: row.background },
      ratio: Math.floor(ratio * 100 + 1e-9) / 100,
      min: row.min,
    };
    (row.level === "error" ? errors : warnings).push(issue);
  }

  return { errors, warnings };
}

/**
 * Проверка строки шаблона целиком: версия состава ключей + палитра. Палитра
 * другой версии (после смены `TOKENS_SCHEMA` без data-миграции) не
 * проверяется по чужому составу — это одна понятная ошибка.
 */
export function validateCabinetThemeRecord(theme: {
  scheme: CabinetThemeScheme;
  tokensSchema: number;
  tokens: unknown;
}): CabinetPaletteReport {
  if (theme.tokensSchema !== TOKENS_SCHEMA) {
    return {
      errors: [
        {
          code: "palette_schema_unsupported",
          message: `Палитра записана в версии ${theme.tokensSchema}, а кабинет понимает версию ${TOKENS_SCHEMA}`,
        },
      ],
      warnings: [],
    };
  }
  return validateCabinetPalette(theme.scheme, theme.tokens);
}
